import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import { PHRASES_GENERATOR_MAX_PHRASES } from '../../../constants';
import { Bem } from '../../../service/Bem/Bem';
import { TestBem } from '../../../service/TestBem';
import { GroupId } from '../../../typings/groups';
import { PhrasesSelectorGeneratorGroupPhrases, PhrasesSelectorWizardId } from '../../../typings/phrases';
import { Shape2, Shape2Icon, Shape2Theme } from '../../Shape2/Shape2';
import { PhrasesSelectorGenerateControls } from '../PhrasesSelectorGenerateControls/PhrasesSelectorGenerateControls';
import { PhrasesSelectorGeneratorGroups } from '../PhrasesSelectorGeneratorGroups/PhrasesSelectorGeneratorGroups';

import './PhrasesSelectorGenerator.css';
import * as keyset from './PhrasesSelectorGenerator.i18n';

const i18n = i18nFactory(keyset);

const BLOCK = 'phrases-selector-generator';

export interface PhrasesSelectorGeneratorProps {
    isExcludePhrases: boolean;
    isGenerateDisabled: boolean;
    wizardId: PhrasesSelectorWizardId;
    groupId: GroupId;
    groups: Array<PhrasesSelectorGeneratorGroupPhrases>;
    generatedPhrasesCount: number;
    onGroupAdd: () => void;
    onGroupRemove: (groupIndex: number) => void;
    onGroupPhrasesChange: (groupIndex: number, phrases: PhrasesSelectorGeneratorGroupPhrases) => void;
    onPhrasesGenerate: () => void;
    onExcludePhrasesToggle: () => void;
}

interface PhrasesSelectorGeneratorState {
    isExpanded: boolean;
}

export class PhrasesSelectorGenerator extends React.PureComponent<
    PhrasesSelectorGeneratorProps,
    PhrasesSelectorGeneratorState
> {
    state = {
        isExpanded: true
    };

    render() {
        const { isExpanded } = this.state;

        const note = isExpanded ? i18n('note-expanded-mode') : i18n('note-hidden-mode');

        return (
            <div
                className={classNames(
                    Bem.cls(BLOCK, '', { expanded: isExpanded }),
                    TestBem.cls(BLOCK, '', { expanded: isExpanded })
                )}
            >
                <header className={Bem.cls(BLOCK, 'header')} onClick={this.onToggle}>
                    <h3 className={Bem.cls(BLOCK, 'title')}>{i18n('title')}</h3>
                    <Shape2
                        hoverable={true}
                        height={14}
                        width={14}
                        cls={classNames(Bem.cls(BLOCK, 'toggle-icon'), TestBem.cls(BLOCK, 'toggle-icon'))}
                        theme={Shape2Theme.light}
                        icon={Shape2Icon.verticalArrow}
                    />
                </header>
                <p className={Bem.cls(BLOCK, 'note')}>{note}</p>
                {isExpanded && this.renderContent()}
            </div>
        );
    }

    private renderContent = () => {
        const {
            isExcludePhrases,
            isGenerateDisabled,
            groups,
            generatedPhrasesCount,
            onGroupAdd,
            onGroupRemove,
            onGroupPhrasesChange,
            onPhrasesGenerate,
            onExcludePhrasesToggle
        } = this.props;

        const isShowNoticeMessage = isGenerateDisabled && generatedPhrasesCount !== 0;

        return (
            <div className={Bem.cls(BLOCK, 'content')}>
                <PhrasesSelectorGeneratorGroups
                    groups={groups}
                    generatedPhrasesCount={generatedPhrasesCount}
                    onGroupAdd={onGroupAdd}
                    onGroupRemove={onGroupRemove}
                    onGroupPhrasesChange={onGroupPhrasesChange}
                />
                {isShowNoticeMessage && (
                    <span className={Bem.cls(BLOCK, 'generate-disabled-note')}>
                        {i18n('disabled-note', {
                            count: PHRASES_GENERATOR_MAX_PHRASES
                        })}
                    </span>
                )}
                <PhrasesSelectorGenerateControls
                    className={Bem.cls(BLOCK, 'controls')}
                    generateButtonText={i18n('generate')}
                    isExcludePhrases={isExcludePhrases}
                    isGenerateDisabled={isGenerateDisabled}
                    onGenerate={onPhrasesGenerate}
                    onExcludePhrasesToggle={onExcludePhrasesToggle}
                />
            </div>
        );
    };

    private onToggle = () => this.setState(prevState => ({ isExpanded: !prevState.isExpanded }));
}
